import { useState, useCallback } from 'react';
import { Plus, Search, Filter, Mail, Phone, Bot, UserX, Megaphone } from 'lucide-react';
import { useLeads } from '@/hooks/useLeads';
import type { LeadApi } from '@/hooks/useLeads';
import { LeadDrawer } from '@/components/LeadDrawer';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import type { BadgeVariant } from '@/components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';

const statusMap: Record<string, { label: string; variant: 'info' | 'warning' | 'default' | 'success' | 'danger' }> = {
  novo:        { label: 'Novo',        variant: 'info' },
  contatado:   { label: 'Contactado',  variant: 'warning' },
  qualificado: { label: 'Qualificado', variant: 'default' },
  convertido:  { label: 'Convertido',  variant: 'success' },
  descartado:  { label: 'Perdido',     variant: 'danger' },
};

const sourceMap: Record<string, string> = {
  website:  'Website',
  referral: 'Indicação',
  social:   'Redes Sociais',
  event:    'Evento',
};

const classificationBadge: Record<string, { emoji: string; label: string; variant: BadgeVariant }> = {
  FRIO:       { emoji: '❄️',  label: 'Frio',       variant: 'info' },
  MORNO:      { emoji: '🌡️', label: 'Morno',      variant: 'warning' },
  QUENTE:     { emoji: '🔥',  label: 'Quente',     variant: 'danger' },
  PRIORIDADE: { emoji: '⚡',  label: 'Prioridade', variant: 'success' },
};

function Leads() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedLead, setSelectedLead] = useState<LeadApi | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data, isLoading } = useLeads({
    search: searchTerm || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  });

  const leads = data?.leads || [];

  const openDrawer = useCallback((lead: LeadApi) => {
    setSelectedLead(lead);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ds-text-primary">Leads</h1>
          <p className="text-ds-text-secondary mt-1">
            Gerencie seus leads e oportunidades de negócio
          </p>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Novo Lead
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ds-text-secondary" />
              <Input
                placeholder="Buscar leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-ds-text-secondary" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-ds-surface2 border border-ds-border rounded-lg px-3 py-2 text-sm text-ds-text-primary focus:outline-none focus:ring-2 focus:ring-ds-success/50"
              >
                <option value="all">Todos os status</option>
                <option value="novo">Novo</option>
                <option value="contatado">Contactado</option>
                <option value="qualificado">Qualificado</option>
                <option value="convertido">Convertido</option>
                <option value="descartado">Perdido</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leads table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Leads ({isLoading ? '...' : leads.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ds-success" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-ds-text-secondary text-sm">Nenhum lead encontrado</p>
              <p className="text-ds-text-secondary text-xs mt-1">Cadastre um novo lead ou ajuste os filtros</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Contato</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>IA</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => {
                  const clsBadge = lead.lead_classification ? classificationBadge[lead.lead_classification] : null;

                  return (
                    <TableRow
                      key={lead.id}
                      onClick={() => openDrawer(lead)}
                      className="cursor-pointer"
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-ds-success/10 flex items-center justify-center shrink-0">
                            <span className="text-sm font-medium text-ds-success">
                              {lead.nome.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-ds-text-primary">{lead.nome}</span>
                              {lead.is_trafego_pago && (
                                <Badge variant="warning" className="flex items-center gap-1 text-xs">
                                  <Megaphone className="h-3 w-3" />
                                  {lead.ad_source_app || 'Anúncio'}
                                </Badge>
                              )}
                              {clsBadge && (
                                <Badge variant={clsBadge.variant} size="sm">
                                  {clsBadge.emoji} {clsBadge.label}
                                </Badge>
                              )}
                            </div>
                            {lead.lead_health_score != null && (
                              <span className="text-xs text-ds-text-secondary">
                                Score: {lead.lead_health_score}/100
                              </span>
                            )}
                            {lead.responsavel_nome && (
                              <span className="text-xs text-ds-text-secondary block">
                                Resp: {lead.responsavel_nome}
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm text-ds-text-secondary">
                            <Mail className="h-3.5 w-3.5 shrink-0" />
                            {lead.email}
                          </div>
                          {lead.telefone && (
                            <div className="flex items-center gap-2 text-sm text-ds-text-secondary">
                              <Phone className="h-3.5 w-3.5 shrink-0" />
                              {lead.telefone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-ds-text-secondary">{lead.empresa || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={statusMap[lead.status]?.variant || 'default'}>
                          {statusMap[lead.status]?.label || lead.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-ds-text-secondary capitalize">
                          {lead.is_trafego_pago
                            ? 'Tráfego Pago'
                            : (sourceMap[lead.fonte || ''] || lead.fonte || 'Outro')}
                        </span>
                      </TableCell>
                      <TableCell>
                        {lead.ia_ativo ? (
                          <Bot className="h-5 w-5 text-ds-success" />
                        ) : (
                          <UserX className="h-5 w-5 text-ds-danger" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <LeadDrawer lead={selectedLead} open={drawerOpen} onClose={closeDrawer} />
    </div>
  );
}

export { Leads };
